/**
 * getCroppedImg — aplica crop + rotação + espelhamento numa imagem usando canvas.
 * Preserva transparência quando a origem é PNG/WebP (senão o fundo transparente vira PRETO no JPEG).
 * Faz clamp do crop dentro da imagem original quando o usuário reduz o zoom além da borda,
 * evitando regiões vazias/pretas no resultado.
 * Retorna { blob, mime, ext } para o chamador saber como enviar ao backend.
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (deg) => (deg * Math.PI) / 180;

const rotateSize = (w, h, rotation) => {
  const rot = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rot) * w) + Math.abs(Math.sin(rot) * h),
    height: Math.abs(Math.sin(rot) * w) + Math.abs(Math.cos(rot) * h),
  };
};

// Detecta alpha: dataURL / extensão / content-type via HEAD (fallback).
const detectHasAlpha = async (imageSrc) => {
  if (!imageSrc) return false;
  const lower = imageSrc.toLowerCase();
  if (lower.startsWith('data:image/png') || lower.startsWith('data:image/webp')) return true;
  if (lower.startsWith('data:image/jpeg') || lower.startsWith('data:image/jpg')) return false;
  if (/\.(png|webp|gif)(\?|#|$)/i.test(lower)) return true;
  if (/\.(jpe?g)(\?|#|$)/i.test(lower)) return false;
  // URL sem extensão (ex: /api/files/{id}). Pergunta pro backend.
  try {
    const res = await fetch(imageSrc, { method: 'HEAD' });
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    return ct.includes('png') || ct.includes('webp') || ct.includes('gif');
  } catch {
    // Se der falha (CORS etc.), assume PNG por segurança — não perde transparência e não pinta preto.
    return true;
  }
};

const toBlobPromise = (canvas, mime, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, mime, quality));

export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, flip = { horizontal: false, vertical: false }) {
  const image = await createImage(imageSrc);
  const hasAlpha = await detectHasAlpha(imageSrc);
  const outMime = hasAlpha ? 'image/png' : 'image/jpeg';
  const outQuality = hasAlpha ? undefined : 0.95;
  const outExt = hasAlpha ? 'png' : 'jpg';

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Se vamos exportar JPEG (sem alpha), preenche fundo BRANCO em vez do preto padrão do canvas.
  if (!hasAlpha) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  // Se o crop não faz sentido (nulo, dimensões degeneradas, ou muito menor que a imagem original —
  // caso típico quando o usuário arrasta o zoom para fora com aspect "Livre" e o react-easy-crop
  // reporta um retângulo de 1-2px), retornamos a imagem inteira já rotacionada/espelhada.
  const canvasArea = canvas.width * canvas.height;
  const cropArea = pixelCrop ? Math.max(0, pixelCrop.width) * Math.max(0, pixelCrop.height) : 0;
  const cropTooSmall =
    !pixelCrop ||
    pixelCrop.width < 20 ||
    pixelCrop.height < 20 ||
    (canvasArea > 0 && cropArea / canvasArea < 0.02); // menos de 2% da imagem → provavelmente bug

  if (cropTooSmall) {
    const blob = await toBlobPromise(canvas, outMime, outQuality);
    return { blob, mime: outMime, ext: outExt };
  }

  // Preserva as dimensões que o usuário definiu no cropper (mesmo que o retângulo extrapole a
  // imagem original — cenário típico quando o cliente reduz o zoom para "caber a foto inteira").
  // Desenhamos a imagem com offset negativo dentro do cropCanvas: pixels fora da imagem viram
  // TRANSPARENTES (PNG) ou BRANCOS (JPEG), NUNCA pretos.
  const cropW = Math.max(8, Math.round(pixelCrop.width));
  const cropH = Math.max(8, Math.round(pixelCrop.height));

  const cropCanvas = document.createElement('canvas');
  const cropCtx = cropCanvas.getContext('2d');
  cropCanvas.width = cropW;
  cropCanvas.height = cropH;
  if (!hasAlpha) {
    cropCtx.fillStyle = '#ffffff';
    cropCtx.fillRect(0, 0, cropCanvas.width, cropCanvas.height);
  }
  cropCtx.drawImage(canvas, -Math.round(pixelCrop.x), -Math.round(pixelCrop.y));

  const blob = await toBlobPromise(cropCanvas, outMime, outQuality);
  return { blob, mime: outMime, ext: outExt };
}

// Thumbnail 400×400 com padding branco (JPEG) — mantém compatibilidade nas listagens em bg claro.
export async function generateThumbnail(input) {
  const blob = input?.blob || input;
  const url = URL.createObjectURL(blob);
  const img = await createImage(url);
  const size = 400;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = size;
  canvas.height = size;

  // Se o blob de entrada tem alpha (PNG), preserva; senão pinta branco pra evitar preto.
  const inputIsPng = blob?.type === 'image/png' || blob?.type === 'image/webp';
  const outMime = inputIsPng ? 'image/png' : 'image/jpeg';
  const outQuality = inputIsPng ? undefined : 0.85;
  if (!inputIsPng) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
  }

  const scale = Math.max(size / img.width, size / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  URL.revokeObjectURL(url);
  return new Promise((resolve) => canvas.toBlob(resolve, outMime, outQuality));
}
